import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import { Id } from '@convex/_generated/dataModel'

// Types
export type ContactStatus = 'unread' | 'read'
export type ContactSubmissionId = Id<'contact_submissions'>

export interface ContactSubmission {
  _id: ContactSubmissionId
  name: string
  email: string
  phone?: string
  message: string
  property: {
    id: string
    title: string
    slug: string
  } | null
  status: ContactStatus
  created_at: number
  source_id?: string
  attribution?: { utm_campaign?: string; utm_source?: string }
  crm_state?: 'pending' | 'creating' | 'linked' | 'review'
  crm_lead_id?: number
  crm_issue?: string
}

export interface ContactStats {
  total: number
  unread: number
  read: number
}

/**
 * Hook for fetching contact submissions with optional status filter.
 * Uses Convex's real-time queries - list updates automatically when data changes.
 */
export function useContactSubmissions(status?: ContactStatus) {
  const data = useQuery(api.contact.list, { status })

  return {
    data: data as ContactSubmission[] | undefined,
    isLoading: data === undefined,
    error: null as Error | null,
  }
}

/**
 * Hook for fetching a single contact submission by ID.
 */
export function useContactSubmission(id: ContactSubmissionId | undefined) {
  const data = useQuery(
    api.contact.getById,
    id ? { id } : 'skip'
  )

  return {
    data: data as ContactSubmission | null | undefined,
    isLoading: data === undefined,
    error: null as Error | null,
  }
}

/**
 * Hook for fetching contact statistics.
 * Uses Convex's real-time queries - stats update automatically when submissions change.
 */
export function useContactStats() {
  const data = useQuery(api.contact.stats)

  return {
    data: data as ContactStats | undefined,
    isLoading: data === undefined,
    error: null as Error | null,
  }
}

/**
 * Hook for updating contact submission status.
 */
export function useUpdateContactStatus() {
  const updateStatus = useMutation(api.contact.updateStatus)

  return {
    mutate: async (id: ContactSubmissionId, status: ContactStatus) => {
      return await updateStatus({ id, status })
    },
    mutateAsync: async (id: ContactSubmissionId, status: ContactStatus) => {
      return await updateStatus({ id, status })
    },
  }
}

/**
 * Hook for deleting a contact submission.
 */
export function useDeleteContactSubmission() {
  const remove = useMutation(api.contact.remove)

  return {
    mutate: async (id: ContactSubmissionId) => {
      return await remove({ id })
    },
    mutateAsync: async (id: ContactSubmissionId) => {
      return await remove({ id })
    },
  }
}
